import React, { useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Button, pxToRem } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';

import { RequestState, RequestStateType } from 'src/shared/messaging-kit/RequestState';
import { toastNotificationsService } from 'src/config/rootService';

const StyledTextareaWrapper = styled.div`
    padding: ${pxToRem(10)};
    border: ${pxToRem(2)} solid ${colors.gray05};
    border-radius: ${pxToRem(4)};

    :focus-within {
        border-color: ${colors.gray20};
    }
`;

const DEFAULT_INPUT_HEIGHT = 36;

const StyledTextarea = styled.textarea`
    border: none;
    max-height: ${pxToRem(150)};
    height: ${pxToRem(DEFAULT_INPUT_HEIGHT)};
    resize: none;
    flex: 1;
    width: 100%;
    vertical-align: text-top;
    color: ${colors.gray55};

    &[disabled] {
        cursor: not-allowed;
        background-color: inherit;
    }

    ::placeholder {
        color: ${colors.gray55};
    }

    :focus-visible {
        outline: none;
        color: ${colors.gray85};
    }
`;

const StyledButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: ${pxToRem(10)};
`;

interface CreateMessageProps {
    conversationUuid: string;
    sendMessage: (message) => void;
    sendMessageRequestState: RequestState;
}

export const CreateMessage: React.FC<CreateMessageProps> = ({
    conversationUuid,
    sendMessage,
    sendMessageRequestState,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [message, setMessage] = useState('');
    const [textareaDisabled, setTextareaDisabled] = useState(false);
    const [sendDisabled, setSendDisabled] = useState(false);

    useEffect(() => {
        setMessage('');
        setTextareaDisabled(false);
        setSendDisabled(true);
    }, [conversationUuid]);

    // Handle the send message ui form state based on the request state.
    useEffect(() => {
        if (sendMessageRequestState.state === RequestStateType.IDLE) {
            setMessage('');
            setTextareaDisabled(false);
            setSendDisabled(true);
        } else if (sendMessageRequestState.state === RequestStateType.ERROR) {
            setTextareaDisabled(false);
            setSendDisabled(false);
        } else if (sendMessageRequestState.state === RequestStateType.LOADING) {
            setTextareaDisabled(true);
            setSendDisabled(true);
        }
    }, [sendMessageRequestState]);

    // Handle showing a toast notification if an error occurs.
    // This is explicitly separated from the useEffect above
    // dealing with the form state because I do not want the `t`
    // dependency to ever re-trigger that logic since it is unrelated
    // to it.
    useEffect(() => {
        if (sendMessageRequestState.state === RequestStateType.ERROR) {
            toastNotificationsService.renderErrorToast(
                t('messaging:conversation.error.unable_to_send_message'),
            );
        }
    }, [t, sendMessageRequestState]);

    useEffect(() => {
        setSendDisabled(message.trim().length === 0);
    }, [message]);

    const handleMessageInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);

        if (inputRef.current) {
            inputRef.current.style.height = `${DEFAULT_INPUT_HEIGHT}px`;
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    };

    return (
        <>
            <StyledTextareaWrapper>
                <StyledTextarea
                    aria-label={t('messaging:conversation.create_message.textbox.aria_label')}
                    ref={inputRef}
                    disabled={textareaDisabled}
                    placeholder={t('messaging:conversation.create_message.textbox.placeholder')}
                    value={message}
                    onChange={handleMessageInput}
                />
            </StyledTextareaWrapper>
            <StyledButtonWrapper>
                <Button
                    aria-label={t('messaging:conversation.create_message.send.aria_label')}
                    type="submit"
                    disabled={sendDisabled}
                    onClick={() => sendMessage(message.trim())}
                >
                    {t('messaging:conversation.create_message.send')}
                </Button>
            </StyledButtonWrapper>
        </>
    );
};
